import axiosInstance from "./axiosInstance";
import { HtmlDocRef, Inventory, InventoryCreationRequest, ItemLocation } from "../types/Inventory";
import type { CoverageArea } from "../utils/coverage";
import type { SearchFilter as InventorySearchFilterDTO } from "../types/SearchFilter";

function authHeader(token?: string) {
  if (!token || token.trim().length === 0) return {};
  return { Authorization: `Bearer ${token}` };
}

// Backend returns 'name'; frontend type uses 'inventoryName'. Normalize on the way in.
function normalizeInventory(data: any): Inventory {
  if (data && !data.inventoryName && data.name) {
    data.inventoryName = data.name;
  }
  return data as Inventory;
}

export async function createInventory(
  token: string,
  payload: InventoryCreationRequest
): Promise<Inventory> {
  const response = await axiosInstance.post<any>("/inventories", payload, {
    headers: authHeader(token),
  });
  return normalizeInventory(response.data);
}

export async function getInventoryById(token: string, id: string): Promise<Inventory> {
  const response = await axiosInstance.get<any>(`/inventories/id/${id}`, {
    headers: authHeader(token),
  });
  return normalizeInventory(response.data);
}

/** How many listings (all stores) still reference any of these category ids.
 *  Category-admin only (CATEGORY_DELETE) — used before deleting a category. */
export async function countInventoriesByCategories(
  token: string,
  categoryIds: string[]
): Promise<number> {
  const response = await axiosInstance.get<number>("/inventories/count-by-categories", {
    headers: authHeader(token),
    params: { categoryIds: categoryIds.join(",") },
  });
  return response.data ?? 0;
}

export async function patchInventory(token: string, payload: Record<string, unknown>): Promise<Inventory> {
  const response = await axiosInstance.patch<any>("/inventories", payload, {
    headers: authHeader(token),
  });
  return normalizeInventory(response.data);
}

export async function deleteInventoryById(token: string, id: string): Promise<void> {
  await axiosInstance.delete(`/inventories/id/${id}`, {
    headers: authHeader(token),
  });
}

/**
 * Uploads a single HTML document (images expected inline as data: URIs) and attaches
 * it to the item. The backend stores it, sets the item's `url` to a public share link
 * and bumps the version — callers should reload the item afterwards.
 */
export async function uploadInventoryHtml(
  token: string,
  id: string,
  version: number,
  file: File
): Promise<HtmlDocRef> {
  const form = new FormData();
  form.append("file", file);
  const response = await axiosInstance.post<HtmlDocRef>(
    `/inventories/id/${id}/html?version=${version}`,
    form,
    // Let the browser set the multipart boundary; override the json instance default.
    { headers: { ...authHeader(token), "Content-Type": "multipart/form-data" } }
  );
  return response.data;
}

export async function deleteInventoryHtml(
  token: string,
  id: string,
  version: number
): Promise<Inventory> {
  const response = await axiosInstance.delete<any>(
    `/inventories/id/${id}/html?version=${version}`,
    { headers: authHeader(token) }
  );
  return normalizeInventory(response.data);
}

// ---- Listing photos (separate GridFS assets) ----

/** Public URL for a listing photo's bytes (usable directly as an <img src>). */
export function photoUrl(inventoryId: string, photoId: string): string {
  return resolveInventoryUrl(`/inventories/id/${inventoryId}/photos/${photoId}`);
}

/** Uploads one photo; returns its ref. Bumps the item version — reload afterwards. */
export async function uploadInventoryPhoto(
  token: string,
  id: string,
  version: number,
  file: File
): Promise<{ photoId: string; contentType?: string; order: number }> {
  const form = new FormData();
  form.append("file", file);
  const response = await axiosInstance.post(
    `/inventories/id/${id}/photos?version=${version}`,
    form,
    { headers: { ...authHeader(token), "Content-Type": "multipart/form-data" } }
  );
  return response.data;
}

export async function deleteInventoryPhoto(
  token: string,
  id: string,
  photoId: string,
  version: number
): Promise<Inventory> {
  const response = await axiosInstance.delete<any>(
    `/inventories/id/${id}/photos/${photoId}?version=${version}`,
    { headers: authHeader(token) }
  );
  return normalizeInventory(response.data);
}

export async function reorderInventoryPhotos(
  token: string,
  id: string,
  version: number,
  photoIdsInOrder: string[]
): Promise<Inventory> {
  const response = await axiosInstance.patch<any>(
    `/inventories/id/${id}/photos/order?version=${version}`,
    photoIdsInOrder,
    { headers: authHeader(token) }
  );
  return normalizeInventory(response.data);
}

// ---- Service-area coverage ----

/** Replaces the listing's whole coverage list. */
export async function setInventoryServiceArea(
  token: string,
  id: string,
  version: number,
  coverage: CoverageArea[]
): Promise<Inventory> {
  const response = await axiosInstance.patch<any>(
    "/inventories",
    { id, version, patchTask: "SetServiceArea", coverage },
    { headers: authHeader(token) }
  );
  return normalizeInventory(response.data);
}

// ---- Fixed item location ----

/** Sets (or clears, with null) the listing's fixed item location. */
export async function setInventoryItemLocation(
  token: string,
  id: string,
  version: number,
  location: ItemLocation | null
): Promise<Inventory> {
  const response = await axiosInstance.patch<any>(
    "/inventories",
    { id, version, patchTask: "SetItemLocation", location },
    { headers: authHeader(token) }
  );
  return normalizeInventory(response.data);
}

/** True when the item's url points at an attached HTML document (vs an external link). */
export function isHtmlDocUrl(url?: string): boolean {
  return !!url && url.includes("/public/inventories/html/");
}

/** Resolves a possibly-relative share url against the API base so it is openable. */
export function resolveInventoryUrl(url: string): string {
  if (/^https?:\/\//i.test(url)) return url;
  const base = (axiosInstance.defaults.baseURL ?? "").replace(/\/+$/, "");
  return `${base}${url.startsWith("/") ? "" : "/"}${url}`;
}

/**
 * Builds a clickable href for an item's url, covering: absolute external links,
 * relative share links (resolved against the API base) and bare domains.
 */
export function inventoryHref(url: string): string {
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("/")) return resolveInventoryUrl(url);
  return `https://${url}`;
}

export async function searchInventories(
  token: string,
  payload: InventorySearchFilterDTO
): Promise<Inventory[]> {
  const response = await axiosInstance.post<Inventory[]>("/inventories/search", payload, {
    headers: authHeader(token),
  });
  return response.data ?? [];
}
